/**
 * Offline-aware Categories Hook
 * Uses SQLite when offline in Electron, falls back to Supabase when online
 * 
 * KEY FIX: Maintains a cache of online data to prevent empty state during offline transition
 */

import { useCallback, useEffect, useState, useRef } from 'react';
import { useNetwork } from '@/contexts/NetworkContext';
import { useElectron } from '@/hooks/useElectron';
import { useCategories, Category } from '@/hooks/useCategories';
import { offlineLog, logCacheRead, logOfflineMode } from '@/utils/offlineDebug';

const COMPONENT = 'OfflineCategories';

interface OfflineCategoriesResult {
  categories: Category[];
  loading: boolean;
  error: string | null;
  isOffline: boolean;
  isFromLocalDb: boolean;
  refetch: () => Promise<void>;
}

export function useOfflineCategories(branchId?: string): OfflineCategoriesResult {
  const { isOnline, isElectronApp } = useNetwork();
  const { database } = useElectron();
  
  // Determine if we should use offline mode - BEFORE calling online hook
  const shouldUseOffline = isElectronApp && !isOnline && !!database;
  
  // State for offline data
  const [offlineCategories, setOfflineCategories] = useState<Category[]>([]);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [offlineError, setOfflineError] = useState<string | null>(null);
  
  // CRITICAL: Track if offline data is ready (has been fetched at least once)
  const [offlineDataReady, setOfflineDataReady] = useState(false);
  
  // CRITICAL: Cache online data so we can show it during offline transition
  const cachedOnlineDataRef = useRef<Category[]>([]);
  
  // Use the standard hook for online mode - DISABLED when offline to prevent network errors
  const onlineResult = useCategories(branchId, { enabled: !shouldUseOffline });
  
  // Cache online data whenever it's available
  useEffect(() => {
    if (!shouldUseOffline && onlineResult.categories && onlineResult.categories.length > 0) {
      cachedOnlineDataRef.current = onlineResult.categories;
      offlineLog('info', `Cached ${onlineResult.categories.length} categories for offline transition`, { component: COMPONENT, branchId });
    }
  }, [shouldUseOffline, onlineResult.categories, branchId]);
  
  // Debug logging for mode detection
  useEffect(() => {
    logOfflineMode(COMPONENT, shouldUseOffline, {
      isOnline,
      isElectronApp,
      hasDatabase: !!database,
      branchId
    });
  }, [shouldUseOffline, isOnline, isElectronApp, database, branchId]);
  
  // Fetch from local SQLite database
  const fetchOfflineCategories = useCallback(async () => {
    if (!branchId || !database) {
      offlineLog('warn', 'Skipping fetch - missing branchId or database', { component: COMPONENT, branchId });
      setOfflineCategories([]);
      return;
    }
    
    setOfflineLoading(true);
    setOfflineError(null);
    
    try {
      offlineLog('info', `Fetching categories from SQLite for branch: ${branchId}`, { component: COMPONENT, branchId });
      const data = await database.getCategories(branchId);
      
      // Transform SQLite data to match Category interface
      const categories: Category[] = (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        branch_id: row.branch_id as string,
        name: row.name as string,
        description: row.description as string | null,
        image_url: row.image_url as string | null,
        sort_order: row.display_order as number,
        is_active: row.is_active === 1,
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
      }));
      
      logCacheRead(COMPONENT, 'categories', categories.length, 'sqlite', branchId);
      
      if (categories.length === 0) {
        offlineLog('warn', 'SQLite returned 0 categories - menu may not be prefetched', { component: COMPONENT, branchId });
      }
      
      setOfflineCategories(categories);
      setOfflineDataReady(true); // Mark offline data as ready
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load offline categories';
      setOfflineError(errorMessage);
      offlineLog('error', `Error fetching from SQLite: ${errorMessage}`, { component: COMPONENT, branchId }, err);
    } finally {
      setOfflineLoading(false);
    }
  }, [branchId, database]);
  
  // Fetch offline data when needed
  useEffect(() => {
    if (shouldUseOffline) {
      fetchOfflineCategories();
    } else {
      // Reset offline data ready state when going online
      setOfflineDataReady(false);
    }
  }, [shouldUseOffline, fetchOfflineCategories]);
  
  // Also sync offline data in background when online (for cache warming)
  useEffect(() => {
    if (isElectronApp && isOnline && database && branchId && onlineResult.categories.length > 0) {
      // Data is synced via the sync engine, but we can trigger a refresh
      // This is handled by the menu prefetch system
    }
  }, [isElectronApp, isOnline, database, branchId, onlineResult.categories]);
  
  // Return appropriate result based on mode
  if (shouldUseOffline) {
    // CRITICAL: If offline data isn't ready yet, use cached online data to prevent empty state
    const categoriesToReturn = offlineDataReady ? offlineCategories : 
      (offlineCategories.length > 0 ? offlineCategories : cachedOnlineDataRef.current);
    
    // Still loading if we're trying to fetch offline data and have neither offline nor cached data
    const isStillLoading = offlineLoading || (!offlineDataReady && offlineCategories.length === 0 && cachedOnlineDataRef.current.length === 0);
    
    offlineLog('info', `Returning offline mode: ${categoriesToReturn.length} categories, ready=${offlineDataReady}, loading=${isStillLoading}`, { component: COMPONENT, branchId });
    
    return {
      categories: categoriesToReturn,
      loading: isStillLoading,
      error: offlineError,
      isOffline: true,
      isFromLocalDb: offlineDataReady,
      refetch: fetchOfflineCategories,
    };
  }
  
  return {
    categories: onlineResult.categories || [],
    loading: onlineResult.loading,
    error: onlineResult.error,
    isOffline: false,
    isFromLocalDb: false,
    refetch: onlineResult.refetch,
  };
}

export default useOfflineCategories;
